import React, { RefObject } from "react";
import {
  Backdrop,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { AddCommentRounded, CloseRounded } from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import AiChatHistoryThread from "./AiChatHistoryThread";
import { useAiChat } from "../../provider/AiChatProvider";
import aiChatHistoryStyles from "./aiChatHistoryStyles";
import { AiChatThread } from "../../types/AIChat/AiChatThread";

type AiChatHistoryProps = {
  open: boolean;
  closeHistory: Function;
  chatWrapperRef: RefObject<HTMLElement | null>;
  backofficeCaseIri: string;
};

export default function AiChatHistory({ open, closeHistory, chatWrapperRef, backofficeCaseIri }: AiChatHistoryProps) {
  const { resetAiChat, activeAgent } = useAiChat();
  const queryClient = useQueryClient();

  const threadFilters = { backofficeCase: backofficeCaseIri, agent: activeAgent };
  const threadsQueryKey = queryKeys.collection("ai_chat_threads", threadFilters);

  const { data: threadCollection } = useQuery<HydraCollectionResult<AiChatThread>>({
    queryKey: threadsQueryKey,
    queryFn: () => fetchCollection("ai_chat_threads", threadFilters),
    enabled: open && !!backofficeCaseIri
  });

  const refreshThreads = async () => {
    await queryClient.invalidateQueries({
      queryKey: threadsQueryKey
    });
  };

  const threads = threadCollection?.["hydra:member"];

  return (
    <>
      <Backdrop open={open} onClick={() => closeHistory()} sx={{ position: "absolute" }} />
      <Slide direction={"right"} in={open} container={chatWrapperRef.current} unmountOnExit>
        <Paper sx={aiChatHistoryStyles.paper}>
          <Box sx={aiChatHistoryStyles.container}>
            <Toolbar>
              <Stack direction={"row"} sx={{ width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant={"subtitle1"}>Chats</Typography>
                <IconButton onClick={() => closeHistory()}>
                  <CloseRounded />
                </IconButton>
              </Stack>
            </Toolbar>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => resetAiChat(false)}>
                  <ListItemIcon>
                    <AddCommentRounded />
                  </ListItemIcon>
                  <ListItemText primary="Neuer Chat" secondary="Starte eine neue Unterhaltung für diese Akte." />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </Box>
          <List>
            {threads?.map((thread) => (
              <AiChatHistoryThread key={thread.id} thread={thread} refreshThreads={refreshThreads} />
            ))}
          </List>
        </Paper>
      </Slide>
    </>
  );
}
